<template>

  <td>
    <div>{{ fileData.file_description }}</div>
  </td>

  <td>
    <div>{{ fileData.file_type }}</div>
  </td>

  <td v-if="getAccessType !== 'customer'">
    <div>{{ formatFileSize(fileData.file_size) }}</div>
  </td>

  <td v-if="getAccessType !== 'customer'">
    <div>{{ fileData.upload_time }}</div>
  </td>

  <td class="sticky-column">

    <div v-if="loading" class="is-flex is-justify-content-center is-align-items-center" style="height: 120px;">
      <div class="spinner"></div>
    </div>

    <!-- Video display block -->
    
    <!-- New version with modal -->

    <div v-else-if="isVideo">
      <div v-if="!videoBlobUrl && !loading" class="icon-wrapper has-text-primary" @click="loadBlob" title="Load Video">
        <i class="fas fa-play-circle fa-2x"></i>
      </div>
      <div v-else
            class="video-preview"
            @click="openVideoModal"
            title="Click to enlarge Video">
          <video
          ref="videoPlayer"
          :src="videoBlobUrl"
          autoplay
          muted
          playsinline
          preload="metadata"
          style="max-width: 100%; height: auto;"
          @contextmenu.prevent
        ></video>
      </div>
    </div>

  
    <!-- PDF display block -->

    <!-- New version with modal -->

    <div v-else-if="isPdf" class="pdf-container">
      <div v-if="!pdfBlobUrl && !loading" class="icon-wrapper has-text-primary" @click="loadBlob" title="Load PDF">
        <i class="fas fa-file-pdf fa-2x"></i>
      </div>
      <div v-else @click="openPdfModal" class="pdf-container" title="Click to enlarge PDF">
        <iframe :src="pdfBlobUrl" width="200" height="100" />
      </div>
    </div>

    <!-- Image display block -->
    <div v-else-if="isImage">
      <div v-if="!imageBlobUrl && !loading" class="icon-wrapper has-text-primary" @click="loadBlob" title="Load Image">
        <i class="fas fa-image fa-2x"></i>
      </div>
      <div v-else class="image-container" title="Click to enlarge Image">
        <img
          :src="imageBlobUrl"
          alt="Preview"
          style="max-width: 200px; height: auto; cursor: zoom-in;"
          @click="openImageModal"
          @contextmenu.prevent
        />
      </div>
    </div>

    <div v-else-if="!isVideo && !isPdf && !isImage">
      <div class="icon-wrapper has-text-primary" @click.prevent="downloadFileHandler" :title="$t('FilesData.Download')">
        <i :class="getFileIcon(fileData.file_name)" class="fa-2x"></i>
      </div>
    </div>


    <div v-else>
      {{ $t('FilesData.Loading') }}
    </div>
  </td>

  <td v-if="getAccessType !== 'customer'">
    <div class="is-flex is-justify-content-start" style="gap: 1em;">
      <div v-if="getEditingSpecialty">
        <button type="button" class="button is-danger is-small" @click="removeFile">
          Delete
        </button>
      </div>
    </div>
  </td>

  <!-- Image Modal -->
     
  <div v-if="showImageModal" class="modal is-active image-modal">
    <div class="modal-background" @click.stop></div>
    <div class="modal-content">
      <div ref="imageZoomContainer">
        <img
          :src="imageBlobUrl"
          alt="Full-size image"
          class="full-image"
          @contextmenu.prevent
        />
      </div>
    </div>
    <button class="modal-close is-large" aria-label="close" @click="closeImageModal"></button>
  </div>

  <!-- PDF Modal -->
  <div v-if="showPdfModal" class="modal is-active pdf-modal">
    <div class="modal-background"></div>
    <div class="modal-content">
      <iframe
        :src="pdfBlobUrl"
        style="border:none; width: 100%; height: 100%;"
      />
    </div>
    <button class="modal-close is-large" aria-label="close" @click="closePdfModal"></button>
  </div>

  <!-- Video Modal -->
  <div v-if="showVideoModal" class="modal is-active video-modal">
    <div class="modal-background"></div>
    <div class="modal-content">
      <video
        ref="modalVideoPlayer"
        :src="videoBlobUrl"
        controls
        autoplay
        muted
        playsinline
        controlsList="nodownload"
        style="width: 100%; height: 100%;"
        @contextmenu.prevent
      ></video>
    </div>
    <button class="modal-close is-large" aria-label="close" @click="closeVideoModal"></button>
  </div>

</template>

<script lang="ts">
import { defineComponent, ref, nextTick } from 'vue'
import { mapGetters, mapActions } from 'vuex'
import Plyr from 'plyr'
import 'plyr/dist/plyr.css'
import { Notyf } from 'notyf'
import panzoom from 'panzoom'

const notyf = new Notyf()

export default defineComponent({
  name: 'SpecialtyDataFileTableRow',

  props: {
    fileData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      videoBlobUrl: undefined as string | undefined,
      pdfBlobUrl: undefined as string | undefined,
      imageBlobUrl: undefined as string | undefined,
      loading: false,
      plyrInstance: null as Plyr | null,
      abortController: null as AbortController | null,
      showImageModal: false as boolean,
      showPdfModal: false as boolean,
      showVideoModal: false as boolean,
      imagePanzoomInstance: null as ReturnType<typeof panzoom> | null,
    }
  },

  computed: {
    ...mapGetters(['getAccessType', 'getEditingSpecialty']),

    isVideo(): boolean {
      return (
        this.fileData.file_type?.startsWith('video/') ||
        /\.(mp4|webm|ogg)$/i.test(this.fileData.file_name)
      )
    },

    isPdf(): boolean {
      return (
        this.fileData.file_type === 'application/pdf' ||
        /\.pdf$/i.test(this.fileData.file_name)
      )
    },

    isImage(): boolean {
      return (
        /^image\//.test(this.fileData.file_type) ||
        /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(this.fileData.file_name)
      )
    },
  },

  methods: {
    ...mapActions(['playFile', 'downloadFile', 'saveSpecialtyFileToBeDeleted', 'removeFileEditedSpecialty']),

    openVideoModal() {
      if (this.videoBlobUrl) {
        this.showVideoModal = true;
      }
    },

    closeVideoModal() {
      this.showVideoModal = false;
      // Optional: pause video when modal closes
      const videoEl = this.$refs.modalVideoPlayer as HTMLVideoElement | undefined;
      if (videoEl) {
        videoEl.pause();
      }
    },
    
    openPdfModal()
    {
      if (this.pdfBlobUrl) {
        this.showPdfModal = true
      }
    },

    closePdfModal()
    {
      this.showPdfModal = false
    },
    
    async openImageModal() {
      if (this.imageBlobUrl) {
        this.showImageModal = true;

        await nextTick();
        const imageContainer = this.$refs.imageZoomContainer as HTMLElement;
        if (imageContainer && !this.imagePanzoomInstance) {
          this.imagePanzoomInstance = panzoom(imageContainer, {
            maxZoom: 5,
            minZoom: 1,
            bounds: true,
            boundsPadding: 0.1,
          });
        }
      }
    },

    closeImageModal() {
      this.showImageModal = false
      if (this.imagePanzoomInstance) {
        this.imagePanzoomInstance.dispose()
        this.imagePanzoomInstance = null
      }
    },

    getFileIcon(fileName: string): string
    {
      const extension = fileName.split('.').pop()?.toLowerCase()

      switch (extension)
      {
        case 'pdf':
          return 'fas fa-file-pdf'
        case 'mp4':
        case 'mov':
        case 'avi':
        case 'webm':
          return 'fas fa-file-video'
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'webp':
          return 'fas fa-file-image'
        case 'doc':
        case 'docx':
          return 'fas fa-file-word'
        case 'xls':
        case 'xlsx':
        case 'csv':
          return 'fas fa-file-excel'
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
          return 'fas fa-file-archive'
        default:
          return 'fas fa-file' // Generic fallback
      }
    },

    async loadBlob() {

      if (this.loading) return
      this.loading = true
      if (this.abortController)
      {
        this.abortController.abort()
      }
      this.abortController = new AbortController()
      this.videoBlobUrl = undefined
      this.pdfBlobUrl = undefined

      try
      {
        if (this.isVideo || this.isPdf || this.isImage)
        {
          const url = await this.playFile({ fileUrl: this.fileData.download_url, signal: this.abortController.signal })
          if (this.isVideo) this.videoBlobUrl = url
          if (this.isPdf) this.pdfBlobUrl = url
          if (this.isImage) this.imageBlobUrl = url

          if (this.isVideo && this.$refs.videoPlayer && !this.plyrInstance) {
            await nextTick()
            const videoEl = this.$refs.videoPlayer as HTMLVideoElement
            if (videoEl)
            {
              this.plyrInstance = new Plyr(videoEl)
              try
              {
                videoEl.muted = true // Ensure video is muted for autoplay
                videoEl.load()
                await videoEl.play()
              }
              catch (err)
              {
                console.warn('Autoplay failed:', err)
              }

            }
          }
        }
      }
      catch (err: unknown)
      {
        if (err instanceof Error)
        {
          if (err.name === 'AbortError') {
            // fetch was aborted, silently ignore
          } else {
            // handle other errors
            console.error(err.message)
          }
        }
        else
        {
          // err is not an Error instance, maybe log it raw
          console.error(err)
        }
      }
      finally
      {
        this.loading = false
        this.abortController = null
      }
    },

    downloadFileHandler() {
      this.downloadFile({ fileUrl: this.fileData.download_url, fileName: this.fileData.file_name })
    },

    async removeFile() {
      if (confirm(this.$t('FilesData.ConfirmDelete'))) {
        if (this.fileData.file_id !== 0) {
          await this.saveSpecialtyFileToBeDeleted(this.fileData.file_id)
        }
        try {
          await this.removeFileEditedSpecialty(this.fileData.file_pos)
          // assuming you use Notyf instance globally via plugin or prototype
          notyf.success(this.$t('FilesData.NotyfFileRemoved'))
        } catch {
          notyf.error(this.$t('FilesData.NotyfErrorRemoveFile'))
        }
      }
    },

    formatFileSize(size: number) {
      if (size < 1024) return `${size} B`
      if (size < 1048576) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1048576).toFixed(1)} MB`
    },

    openPdfInNewTab() {
      if (this.pdfBlobUrl) {
        window.open(this.pdfBlobUrl, '_blank')
      }
    },
  },

  /*
  mounted() {
    this.loadBlob()
  },
  */

  beforeUnmount() {
    if (this.plyrInstance) {
      this.plyrInstance.destroy()
      this.plyrInstance = null
    }
    
    if (this.videoBlobUrl)
    {
      URL.revokeObjectURL(this.videoBlobUrl)
      this.videoBlobUrl = undefined
    }

    if (this.pdfBlobUrl)
    {
      URL.revokeObjectURL(this.pdfBlobUrl)
      this.pdfBlobUrl = undefined
    }

    if (this.imageBlobUrl)
    {
      URL.revokeObjectURL(this.imageBlobUrl)
      this.imageBlobUrl = undefined
    } 
  },

  /*
  watch:
  {
    fileData:
    {
      handler()
      {
        //this.loadBlob()
      },
      immediate: true,
      deep: true,
    },
  },
  */

})
</script>

<style scoped>
  .pointer {
    cursor: pointer;
  }
  .sticky-column {
    position: sticky;
    left: 0;
    width: 200px;
    max-width: 200px;
    min-width: 200px;
    background: #fff;
    z-index: 1;
    overflow: hidden;
    border-right: 1px solid #ddd;
    border-left: 1px solid #ddd;
  }

  .pdf-container {
    cursor: zoom-in;
    position: relative;
    display: inline-block;
  }

  .pdf-container iframe {
    pointer-events: none;
  }


  .maximize-button {
    position: absolute;
    top: 5px;
    left: 5px;
    z-index: 10;
    cursor: pointer;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    border: none;
    padding: 4px 6px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-wrapper {
    cursor: pointer;
    color: #3273dc; /* Bulma primary blue */
    transition: transform 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .icon-wrapper:hover {
    transform: scale(1.1);
    color: #363636;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .spinner {
    border: 8px solid #f3f3f3;
    border-top: 8px solid #23d160; /* Bulma success button color */
    border-radius: 50%;
    width: 60px;
    height: 60px;
    animation: spin 1.5s linear infinite;
  }

  .image-modal {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-content {
    margin: 0 !important;
    padding: 0 !important;
    width: 100%;
    max-width: 100%;
    max-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal.is-active {
    display: flex !important;
    align-items: center;
    justify-content: center;
    height: 100vh;           /* full viewport height */
    max-height: 100vh;       /* limit max height */
    overflow: hidden;        /* avoid scroll outside */
    padding: 1rem;           /* optional spacing around modal content */
    box-sizing: border-box;
  }

  .full-image {
    max-width: 100vw;
    max-height: 95vh;
    width: auto;
    height: auto;
    object-fit: contain;
    cursor: grab;
    display: block;
    margin: auto;
  }

    .full-image:active {
    cursor: grabbing;
  }


  .pdf-modal .modal-content {
    margin: 0 !important;
    padding: 0 !important;
    width: 90vw;
    max-width: 1200px;
    height: 90vh;           /* explicitly fix height */
    max-height: 90vh;
    display: flex;
    /* Remove vertical centering */
    align-items: stretch; /* make iframe fill vertical space */
    justify-content: center;
  }

  .video-modal .modal-content {
    margin: 0 !important;
    padding: 0 !important;
    width: 90vw;
    max-width: 1200px;
    height: 90vh;
    max-height: 90vh;
    display: flex;
    align-items: stretch;
    justify-content: center;
  }

  .video-modal video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .video-preview {
  position: relative;
  max-width: 200px;
  cursor: zoom-in;
  }

  .video-preview video {
    display: block;
    width: 100%;
    height: auto;
  }

  .video-overlay-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    color: rgba(255, 255, 255, 0.8);
    text-shadow: 0 0 4px #000;
  }

</style>
